import React, { useState, useEffect } from "react";
import { firestore, auth } from "../firebaseConfig";
import { collection, getDocs, query, where, updateDoc, doc, deleteDoc, getDoc } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import BillingManagement from "./BillingManagement";
import BillingPage from "./BillingPage";
import { analytics } from "../firebaseConfig";
import { logEvent } from "firebase/analytics";
const ManageMembers = () => {
  const [user] = useAuthState(auth);
  const [orgId, setOrgId] = useState(null);
  const [role, setRole] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [subscribed, setSubscribed] = useState(false)
  const functions = getFunctions();


  if (process.env.NODE_ENV === "development") {
    connectFunctionsEmulator(functions, "localhost", 5001);
  }

  useEffect(() => {
    logEvent(analytics, 'manage_page');
    if (user) {
      const fetchUser = async () => {
        const userQuery = doc(firestore, "users", user.uid)
        const userSnapshot = await getDoc(userQuery);
        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();
          const isSubscribed = !!userData.subscriptionActive;
          setOrgId(userData.orgId);
          setRole(userData.role);
          setSubscribed(isSubscribed);
        }
      };
      fetchUser();
    }
  }, [user]);

  useEffect(() => {
    if (orgId) {
      const fetchMembersAndInvites = async () => {
        setLoading(true);
        const membersQuery = query(collection(firestore, "users"), where("orgId", "==", orgId));
        try {
          const membersSnapshot = await getDocs(membersQuery);
          setMembers(membersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          // console.log("✅ Members query succeeded");
        } catch (e) {
          // console.error("❌ Members query failed", e);
        }


        const invitesQuery = query(collection(firestore, "invites"), where("orgId", "==", orgId), where("status", "==", "pending"));
        try {
          const invitesSnapshot = await getDocs(invitesQuery);
          // setInvites(invitesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          // Create a map to store the latest invite per email
          const latestInvitesMap = new Map();

          invitesSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const existing = latestInvitesMap.get(data.email);

            if (!existing || data.timestamp > existing.timestamp) {
              latestInvitesMap.set(data.email, { id: doc.id, ...data });
            }
          });

          // Convert the map to an array and set the invites
          setInvites(Array.from(latestInvitesMap.values()));
          // console.log("✅ Invites query succeeded");
        } catch (e) {
          // console.error("❌ Invites query failed", e);
        }


        setLoading(false);
      };
      fetchMembersAndInvites();
    }
  }, [orgId]);

  const activeMembers = members.length;

  const handleRoleChange = async (memberId, newRole) => {
    if (activeMembers === 1) {
      alert("The last active member must always be an admin.");
      return;
    }

    await updateDoc(doc(firestore, "users", memberId), {
      role: newRole,
    });
    setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m)));
  };

  const handleRemoveMember = async (memberId) => {
    if (activeMembers === 1) {
      alert("The last active member cannot be removed.");
      return;
    }

    await deleteDoc(doc(firestore, "users", memberId));
    setMembers((prev) => prev.filter((m) => m.id !== memberId));
  };

  const handleSendInvite = async (email) => {
    if (!email || !orgId) return;
    setInviting(true);

    try {
      const sendInvite = httpsCallable(functions, "sendInvite");
      await sendInvite({ email, orgId, inviterEmail: user.email });

      const newInvite = {
        id: Math.random().toString(36).substr(2, 9),
        email,
        orgId,
        status: "pending",
        createdAt: new Date(),
      };
      setInvites((prev) => [...prev, newInvite]);

      alert("Invitation sent successfully!");
      setNewMemberEmail("");
    } catch (error) {
      console.error("Error sending invite:", error);
      alert("Failed to send invitation.");
    }

    setInviting(false);
  };

  if (role !== "admin") {
    return <>
    {subscribed ? <BillingManagement />: <BillingPage />}
    </>;
  }

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg mt-8">
      <h2 className="text-2xl font-semibold mb-4 text-center text-blue-600 dark:text-blue-400">Manage Members</h2>

      {/* Invite Members */}
      <div className="flex space-x-4">
        <input
          type="email"
          placeholder="Enter member email"
          className="w-full p-2 border dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
          value={newMemberEmail}
          onChange={(e) => setNewMemberEmail(e.target.value)}
        />
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={() => handleSendInvite(newMemberEmail)}
          disabled={inviting}
        >
          {inviting ? "Sending..." : "Invite"}
        </button>
      </div>

      {/* Pending Invitations */}
      <h3 className="mt-6 text-lg font-semibold text-gray-700 dark:text-gray-300">Pending Invitations</h3>
      {invites.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">No pending invites.</p>
      ) : (
        <table className="w-full border-collapse bg-white dark:bg-gray-800 shadow-md rounded-lg mt-4">
          <thead>
            <tr className="bg-gray-200 dark:bg-gray-700">
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Sent On</th>
              <th className="p-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invites.map((invite) => (
              <tr key={invite.id} className="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                <td className="p-3">{invite.email}</td>
                <td className="p-3">{new Date(invite.createdAt * 1000).toLocaleDateString()}</td>
                <td className="p-3">
                  <button
                    className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                    onClick={() => handleSendInvite(invite.email)}
                  >
                    Re-send
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Current Members */}
      <h3 className="mt-6 text-lg font-semibold text-gray-700 dark:text-gray-300">Current Members</h3>
      <table className="w-full border-collapse bg-white dark:bg-gray-800 shadow-md rounded-lg mt-4">
        <thead>
          <tr className="bg-gray-200 dark:bg-gray-700">
            <th className="p-3 text-left">Email</th>
            <th className="p-3 text-left">Role</th>
            <th className="p-3 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <tr key={member.id} className="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
              <td className="p-3">{member.email}</td>
              <td className="p-3">
                <select
                  className="p-2 border dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                  value={member.role}
                  onChange={(e) => handleRoleChange(member.id, e.target.value)}
                  disabled={activeMembers === 1 && member.role === "admin"}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </td>
              <td className="p-3">
                {member.uid !== user.uid && (
                  <button
                    className="px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600"
                    onClick={() => handleRemoveMember(member.id)}
                    disabled={activeMembers === 1}
                  >
                    Remove
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {subscribed ? <BillingManagement />: <BillingPage />}
    </div>
  );

};

export default ManageMembers;
